import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { staffApi } from "../Apis/Api";
import LoadingComponent from "../components/ui/LoadingComponent";
import useDebounce from "../hook/useDebounce";
import TableDataNoAvailable from "../components/ui/TableDataNoAvailable";
import { formatDateTime } from "../utils/formatDateTime";
import PaginationControls from "../components/ui/PaginationControls";
import Breadcrumb from "../components/Breadcrumb";
import { usePermissions } from "../context/PermissionContext";
import CardContainer from "../components/ui/CardContainer";
import PerPageDropdown from "../components/ui/PerPageDropdown";
import ErrorAlert from "../components/ui/ErrorAlert";
import SortIcons from "../components/ui/SortIcons";
import { TextWrap } from "../utils/textWrap";
import { AddButton } from "../components/ui/Button";

const PermissionsPage = () => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({ search: "" });
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const searchInputRef = useRef(null);
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const debouncedSearch = useDebounce(filters.search, 500);

  useEffect(() => {
    if (
      !loading &&
      searchInputRef.current &&
      document.activeElement !== searchInputRef.current
    ) {
      searchInputRef.current.focus();
    }
  }, [loading]);

  const fetchPermissions = async () => {
    setLoading(true);
    setError(null);
    try {
      const fieldMapping = {
        permission_key: "permission_key",
        module_name: "module_name",
        role_name: "role_name",
        created_by: "created_by",
        created_datetime: "created_datetime",
      };

      const params = {
        page: pagination.currentPage,
        per_page: pagination.perPage,
        search: debouncedSearch,
        ...(sortConfig.field && {
          sort_by: fieldMapping[sortConfig.field] || sortConfig.field,
          sort_order: sortConfig.direction,
        }),
      };

      const response = await staffApi.getAllClientPermissions(params);
      if (!Array.isArray(response.data)) {
        throw new Error("Invalid API response: data is not an array");
      }
      if (!response.pagination || typeof response.pagination !== "object") {
        console.warn("Pagination data missing, using defaults");
        response.pagination = {
          current_page: 1,
          per_page: pagination.perPage,
          total: response.data.length,
        };
      }

      const data = response.data.map((permission, index) => ({
        id: permission.client_permission_id || index + 1,
        permission_key: permission.permission_key || "-",
        module_name: permission.module_name || "-",
        role_name: permission.role_name || "-",
        created_by: permission.created_by || "-",
        created_datetime: permission.created_datetime || "-",
        display_created_datetime: permission.created_datetime
          ? formatDateTime(permission.created_datetime)
          : "-",
      }));

      if (sortConfig.field) {
        data.sort((a, b) => {
          const valueA = a[sortConfig.field] || "";
          const valueB = b[sortConfig.field] || "";
          if (sortConfig.field === "created_datetime") {
            const dateA = new Date(a.created_datetime || 0);
            const dateB = new Date(b.created_datetime || 0);
            return sortConfig.direction === "asc"
              ? dateA - dateB
              : dateB - dateA;
          }
          return sortConfig.direction === "asc"
            ? valueA.toString().localeCompare(valueB.toString(), undefined, {
                sensitivity: "base",
              })
            : valueB.toString().localeCompare(valueA.toString(), undefined, {
                sensitivity: "base",
              });
        });
      }

      setPermissions(data);
      setPagination({
        currentPage: response.pagination.current_page || 1,
        perPage: response.pagination.per_page || pagination.perPage,
        total: response.pagination.total || data.length,
      });
      setError(null);
    } catch (err) {
      const errorMessage =
        err?.message ||
        "Failed to fetch permissions. Please check the API endpoint or network connection.";
      setError(errorMessage);
      setPermissions([]);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePerPageChange = (value) => {
    setPagination((prev) => ({ ...prev, perPage: value, currentPage: 1 }));
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  useEffect(() => {
    fetchPermissions();
  }, [pagination.currentPage, pagination.perPage, debouncedSearch, sortConfig]);

  if (error) {
    return <ErrorAlert error={error} onRetry={fetchPermissions} />;
  }

  const headerContent = (
    <div className="container-fluid">
      <div className="row align-items-center">
        <div className="col-12 col-md-6 mb-3 mb-md-0">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <div className="navbar-search flex-grow-1">
              <input
                type="text"
                className="form-control h-40-px w-100"
                name="search"
                placeholder="Search"
                value={filters.search}
                onChange={handleSearchChange}
                ref={searchInputRef}
                aria-label="Search permissions"
              />
              <i className="bi bi-search icon"></i>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 d-flex align-items-center justify-content-between">
          <PerPageDropdown
            perPage={pagination.perPage}
            handlePerPageChange={handlePerPageChange}
          />
          <AddButton
            label="Assign Role Permission"
            permissionKey="add_client_permission"
            onClick={() => navigate("/assign-permission")}
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Breadcrumb title="Staff Permission" />
      <CardContainer
        headerContent={headerContent}
        paginationControls={
          <PaginationControls
            pagination={pagination}
            handlePageChange={handlePageChange}
          />
        }
        loading={loading}
      >
        <div
          className="position-relative"
          style={{
            opacity: loading ? 0.4 : 1,
            pointerEvents: loading ? "none" : "auto",
          }}
        >
          {permissions.length === 0 && !loading ? (
            <TableDataNoAvailable
              message={
                filters.search
                  ? "No permissions match your search."
                  : "No permissions available."
              }
            />
          ) : (
            <table className="table bordered-table sm-table mb-0 table-hover">
              <thead>
                <tr>
                  <th>#</th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("permission_key")}
                    role="button"
                    aria-label={`Sort by permission key, currently sorted ${
                      sortConfig.field === "permission_key"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Permission Key
                      <SortIcons
                        field="permission_key"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("module_name")}
                    role="button"
                    aria-label={`Sort by module name, currently sorted ${
                      sortConfig.field === "module_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Module Name
                      <SortIcons field="module_name" sortConfig={sortConfig} />
                    </div>
                  </th>

                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("created_by")}
                    role="button"
                    aria-label={`Sort by created by, currently sorted ${
                      sortConfig.field === "created_by"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Created By
                      <SortIcons field="created_by" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("created_datetime")}
                    role="button"
                    aria-label={`Sort by created date, currently sorted ${
                      sortConfig.field === "created_datetime"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Created Date
                      <SortIcons
                        field="created_datetime"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {permissions.map((permission, index) => (
                  <tr key={`${permission.id}-${permission.role_name}`}>
                    <td>{index + 1}</td>
                    <TextWrap
                      location={permission.permission_key
                        .split("_")
                        .map(
                          (word) => word.charAt(0).toUpperCase() + word.slice(1)
                        )
                        .join(" ")}
                    />
                    <TextWrap location={permission.module_name} />
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {permission.created_by}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {permission.display_created_datetime}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {loading && (
          <div
            className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
            style={{
              zIndex: 10,
              borderRadius: "10px",
              padding: "20px",
            }}
          >
            <LoadingComponent />
          </div>
        )}
      </CardContainer>
    </>
  );
};

export default PermissionsPage;
